import { HelpSidebar } from '../components/HelpSidebar'
import { HelpSearchBar } from '../components/HelpSearchBar'
import { useHelpManifest } from '../api/queries'
import { BookOpen, Sparkles, ShieldAlert, Users, Search } from 'lucide-react'

/**
 * /help — landing page. Hero + featured topics + the sidebar tree.
 *
 * Operators land here from the Topbar Help button or `g ?` from
 * anywhere in the portal. From here they pick a module, a layer
 * reference page, the glossary, or use search to jump direct to a
 * specific section.
 */
export function HelpIndexPage() {
  const { data: manifest, isLoading, error } = useHelpManifest()

  return (
    <div className="flex h-full flex-col bg-canvas">
        {/* Page header band — title + search */}
        <header className="flex items-center gap-4 border-b border-border-subtle bg-surface px-6 py-3">
          <BookOpen size={20} className="text-accent" />
          <div className="flex-1">
            <h1 className="text-role-page-title">Help</h1>
            <p className="text-role-page-subtitle">
              How CARLA + the SPI ops portal work — workflows, tools, FAQs.
            </p>
          </div>
          <div className="w-96">
            <HelpSearchBar />
          </div>
        </header>

        {/* MasterDetail-style body — left tree, right hero/featured */}
        <div className="flex flex-1 overflow-hidden">
          <aside className="w-[360px] flex-shrink-0 overflow-y-auto border-r border-border-subtle bg-surface-sunk">
            {isLoading ? (
              <div className="p-4 text-sm text-tertiary">Loading…</div>
            ) : error ? (
              <div className="p-4 text-sm text-danger-text">
                Couldn't load the help index. Try refreshing.
              </div>
            ) : manifest ? (
              <HelpSidebar manifest={manifest} />
            ) : null}
          </aside>

          <main className="flex-1 overflow-y-auto px-8 py-6">
            <HelpHero />
          </main>
        </div>
      </div>
  )
}


function HelpHero() {
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section>
        <h2 className="text-role-page-title mb-2">Welcome to the SPI ops help portal.</h2>
        <p className="text-sm leading-relaxed text-secondary">
          CARLA is your AI coworker. She lives in the chat panel and can answer
          questions + take actions across Odoo (sales, purchasing, inventory,
          accounting, regulatory), the SPI MailDesk inbox, the CofA pipeline,
          sample-order projects, and more. This portal documents what she can
          do, who can ask her to do what, and how to phrase requests that
          reliably work.
        </p>
      </section>

      <section className="space-y-3">
        <h3 className="text-role-section-label">Featured guides</h3>
        <div className="grid grid-cols-2 gap-3">
          <FeaturedCard
            icon={<Users size={16} />}
            href="/help/user-guides/sales-inbox"
            title="Sales Inbox"
            blurb="Customer POs arrive here for review. Drag-drop a PDF, CARLA extracts, you confirm lines, draft SO lands in Odoo."
          />
          <FeaturedCard
            icon={<Sparkles size={16} />}
            href="/help/user-guides/quotes"
            title="Quotes"
            blurb="Build a draft quote, run it through pricing review, send to the customer, convert to a sales order on approval."
          />
          <FeaturedCard
            icon={<Sparkles size={16} />}
            href="/help/user-guides/open-orders"
            title="Open Orders"
            blurb="Live view of every active sales order. Filters, columns, and the cross-reference to the linked Odoo PO."
          />
          <FeaturedCard
            icon={<Sparkles size={16} />}
            href="/help/user-guides/margins"
            title="Margins"
            blurb="Booked vs. invoiced margin per order. Reading the columns, grouping by product / customer, exporting."
          />
          <FeaturedCard
            icon={<ShieldAlert size={16} />}
            href="/help/user-guides/cofa-pipeline"
            title="CofA Pipeline"
            blurb="Batch-upload vendor CofAs, walk them through the approval kanban, generate the SPI CofA your customer sees."
          />
          <FeaturedCard
            icon={<Search size={16} />}
            href="/help/user-guides/inventory"
            title="Inventory"
            blurb="Stock + lots side-by-side. Read the on-hand vs. reserved vs. available columns, find a lot, check warehouse health."
          />
        </div>
      </section>

      <section className="rounded-sm border border-border-subtle bg-surface px-4 py-3">
        <h3 className="text-role-section-label mb-1">Tips</h3>
        <ul className="m-0 list-disc pl-5 text-sm leading-relaxed text-secondary">
          <li>Press <kbd className="rounded-xs bg-surface-sunk px-1.5 py-0.5 text-2xs font-semibold">Cmd K</kbd> from anywhere → search across modules + help.</li>
          <li>Press <kbd className="rounded-xs bg-surface-sunk px-1.5 py-0.5 text-2xs font-semibold">g ?</kbd> to jump back to this page.</li>
          <li>Click the <kbd className="rounded-xs bg-surface-sunk px-1.5 py-0.5 text-2xs font-semibold">?</kbd> next to any module's page title to read about that module specifically.</li>
          <li>Every page URL is a permalink — share with your team.</li>
        </ul>
      </section>
    </div>
  )
}


function FeaturedCard({
  icon,
  href,
  title,
  blurb,
}: {
  icon: React.ReactNode
  href: string
  title: string
  blurb: string
}) {
  return (
    <a
      href={href}
      className="block rounded-sm border border-border-subtle bg-surface p-3 transition-colors hover:border-accent hover:bg-surface-hover"
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-accent">
        {icon}
        <span className="text-sm font-semibold text-primary">{title}</span>
      </div>
      <p className="m-0 text-xs leading-snug text-secondary">{blurb}</p>
    </a>
  )
}
